<template>
  <div class="relative mt-16">
    <div class="flex flex-wrap">
      <div
        v-for="image in submittedImages"
        :key="image.id"
        class="mb-24 relative h-auto w-[100px] mr-16"
      >
        <div class="absolute -right-12 -top-12 cursor-pointer bg-white h-24 w-24 rounded-full" @click="removeFile(image.id)">
          <ion-icon
            class="text-error h-24 w-24"
            :icon="closeCircle"
          />
        </div>
        <img :src="image.url">
      </div>
    </div>
    <input
      id="file"
      ref="fileInput"
      class="file-input"
      type="file"
      @change="uploadAsset"
    >
    <Button
      text="Bifoga bild/pdf"
      type="secondary"
      :icon="documentOutline"
      @click="clickButton"
    />
  </div>
</template>

<script lang="ts" setup>
import { IonIcon } from '@ionic/vue';
import { documentOutline, closeCircle } from 'ionicons/icons';
import { computed, ref, watch } from 'vue';
import Button from '@/components/Button.vue';
import { onClickOutside } from '@vueuse/core';
import useApi from '@/composeables/useApi';
import { Asset } from '@/constants/types';

const props = defineProps<{
  placeholder: string;
  subText?: string;
  value?: string;
  assets?: Asset[];
}>();

const emit = defineEmits<{
  (e: 'onInput', payload: string): void
  (e: 'onAssets', payload: Partial<Asset>[]): void
}>();

const { apiPost } = useApi();

const selectorDropdown = ref<HTMLDivElement>();
const fileInput = ref<HTMLInputElement>();
const show = ref(false);
const submittedImages = ref<{ id: number; url: string }[]>([]);
const submittedValue = computed(() => submittedImages.value.map(image => image.id).join('|'));

onClickOutside(selectorDropdown, () => {
  show.value = false;
});

const clickButton = () => {
  if (fileInput.value) {
    fileInput.value.focus();

    setTimeout(() => {
      fileInput.value?.click();
    }, 50);
  }
};

const uploadAsset = async(e: Event & { target: { files: FileList }}) => {
  const file = e.target.files?.[0];
  if (!file) {
    return;
  }

  const fd = new FormData();
  fd.append('file', file, file.name);
  const res = await apiPost<Asset>('/incidents/file', fd, null, false);

  if (res) {
    const newAsset = {
      url: res.url.replace('http://', 'https://'),
      id: res.id,
    };
    submittedImages.value = [...submittedImages.value, newAsset];
    emit('onInput', submittedValue.value);
    emit('onAssets', submittedImages.value);
  }
};

const removeFile = (imageId: number) => {
  submittedImages.value = submittedImages.value.filter(image => image.id !== imageId);
  emit('onInput', submittedValue.value);
  emit('onAssets', submittedImages.value);
};

const syncSubmittedImagesFromAssets = (assets?: Asset[]) => {
  if (!assets?.length) {
    if (!submittedImages.value.length) return;
    submittedImages.value = [];
    return;
  }

  submittedImages.value = assets.map(asset => ({
    id: asset.id,
    url: asset.url.replace('http://', 'https://'),
  }));
};

watch(
  () => props.assets,
  (assets) => {
    syncSubmittedImagesFromAssets(assets);
  },
  { immediate: true }
);
</script>
<style scoped>
.file-input {
  display: none;
}
</style>
